import { Box, Button, Checkbox, FormHelperText, IconButton, Link, Paper, Popper, styled, Tooltip, Typography, useTheme } from "@mui/material";
import { FC, useEffect, useRef } from "react";
import { useDataCatalogContext } from "../dataCatalogContext/DataCatalogContext";
import FieldEdit from "./FieldEdit";
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/DoNotDisturb';
import { BulkValidationError, FieldBulkEditContextProvider } from "./FieldBulkEditContextProvider";
import { useSelector } from "react-redux";
import { ReduxState } from "ra-core";


const PopoutFieldBulkEditRoot = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  position: 'absolute',
  width: 56,
  paddingLeft: 56,
  height: 40,
  left: 0,
  zIndex: 3,

  '&.hasSelected': {
    width: 'calc(100% - 56px)',
  },
}))

const PopoutFieldBulkEdit: FC = () => {
  const containerElRef = useRef<Element|null>(null)
  const anchorElRef = useRef<Element|null>(null)
  const handleSubmitRef = useRef(()=>{})

  useEffect(() => {
    containerElRef.current = document.querySelector('div.MuiContainer-root')
  }, [])

  const { bulkEditCount, bulkEditFullChecked, bulkEditOpen, startBulkEdit, cancelBulkEdit, handleSelectAll, removeManyFromDraft } = useDataCatalogContext()

  const valiErrors = useSelector<ReduxState, BulkValidationError>(store => (store.admin.resources['govrnc-vali-bulk']?.data['1'] as any) || {id:1, failed:[]})

  const handleClick = (event:React.MouseEvent<HTMLButtonElement>) => {
    startBulkEdit()
  };

  const handleRemoveIncompatibleFields = () => {
    if (valiErrors.failed.length > 0) {
      removeManyFromDraft(valiErrors.failed)
    }
  }

  const id = bulkEditOpen ? 'simple-popover' : undefined;

  const theme = useTheme()

  const dialogRef = useRef<HTMLElement|null>(null)
  useEffect(() => {
    if (bulkEditOpen) {
      setTimeout(() => {
        dialogRef.current?.focus()
      })
    }
  }, [bulkEditOpen])

  return (
    <PopoutFieldBulkEditRoot ref={anchorElRef}
      className={bulkEditCount > 0 ? "hasSelected" : ''}
    >
      <Box sx={{ width: 42, px: '7px', }}>
        <Tooltip title="Select All">
          <Checkbox 
            checked={bulkEditFullChecked} 
            indeterminate={bulkEditCount > 0 && !bulkEditFullChecked} 
            onChange={handleSelectAll}
            inputProps={{
              'aria-label': 'Select All'
            }}
          />
        </Tooltip>
      </Box>
      {bulkEditCount > 0 && !bulkEditOpen &&
        <Button onClick={handleClick} component="button" >Bulk Edit Fields with...</Button>
      }
      {bulkEditCount > 0 && bulkEditOpen &&
        <>
          <Tooltip title="Bulk Edit Save">
            <IconButton 
              color="primary" 
              onClick={() => handleSubmitRef.current && handleSubmitRef.current()} 
              disabled={valiErrors.failed.length > 0}
            >
              <SaveIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton color="info" onClick={cancelBulkEdit}>
              <CancelIcon />
            </IconButton>
          </Tooltip>
        </>
      }
      {bulkEditCount > 0 &&
        <Typography variant="body1" color="success" sx={{ ml: 4 }} >{bulkEditCount} selected</Typography>
      }
      <Popper
        id={id}
        open={bulkEditOpen}
        anchorEl={anchorElRef.current}
        container={containerElRef.current}
        placement="bottom"
      >
        <Paper
          role="dialog"
          sx={{ width: 1152, borderRadius: 0, boxShadow: 3, mb: 8, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}
        >
          <FieldBulkEditContextProvider>
            <span tabIndex={-1} ref={dialogRef} className="sr-only"></span>
            <Box sx={{ mx: 4, pt: 1, mb: 1 }}>
              <Box sx={{ borderLeft: `4px solid ${theme.palette.info.main}`, pl: 2 }}>
                <Typography component="span" variant="subtitle2">Bulk Edit Fields with:</Typography>
              </Box>
              {valiErrors.failed.length > 0 &&
                <FormHelperText error sx={{ mt: 2, }}>
                  Some fields can not be applied with specified Encryption Rule settings (Identity/Computed fields or database locked for Encryption Setting Changes).
                  {" "}
                  <Link component="button" onClick={handleRemoveIncompatibleFields}>Click here</Link>
                  {" "}
                  to remove them ({valiErrors.failed.length} rows) from Bulk Editing.
                </FormHelperText>
              }
            </Box>
            <FieldEdit handleSubmitRef={handleSubmitRef} />
            
          </FieldBulkEditContextProvider>
        </Paper>
      </Popper>
    </PopoutFieldBulkEditRoot>
  )
}

export default PopoutFieldBulkEdit;
